import type { Breadcrumb } from '@sentry/capacitor';
import { CONFIGS } from '../config';

// On native, @sentry/capacitor deep-normalises every recorded breadcrumb and sends it across the
// Capacitor bridge on the main thread. Recording breadcrumbs from map clicks froze the map
// (7f4814e154), so only small, known breadcrumb types are kept. Returning null (not false) stops
// a breadcrumb before it reaches the scope and the native sync.

export const MAX_BREADCRUMBS = 50;

// Matches Sentry's own BreadcrumbHint, which @sentry/capacitor does not re-export.
type BreadcrumbHint = { [key: string]: any };

const SENSITIVE_PARAM = /token|key|secret|signature|password|auth|code/i;

const API_HOSTS = new Set(Object.values(CONFIGS).map(config => new URL(config.COREO_API_URL).host));

const isMapClick = (hint?: BreadcrumbHint): boolean => {
  const target = hint?.event?.target;
  return typeof target?.closest === 'function' && target.closest('.mapboxgl-map') !== null;
};

export const scrubUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    for (const key of Array.from(parsed.searchParams.keys())) {
      if (SENSITIVE_PARAM.test(key)) {
        parsed.searchParams.set(key, '[Filtered]');
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

const isApiRequest = (url: unknown): url is string => {
  if (typeof url !== 'string') {
    return false;
  }
  try {
    return API_HOSTS.has(new URL(url).host);
  } catch {
    return false;
  }
};

export const filterBreadcrumb = (breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null => {
  switch (breadcrumb.category) {
    case 'navigation':
    case 'redux.action':
      return breadcrumb;

    case 'ui.click':
      if (isMapClick(hint)) {
        return null;
      }
      return breadcrumb;

    case 'fetch':
    case 'xhr': {
      const url = breadcrumb.data?.url;
      if (!isApiRequest(url)) {
        return null;
      }
      return {
        ...breadcrumb,
        data: {
          method: breadcrumb.data.method,
          url: scrubUrl(url),
          status_code: breadcrumb.data.status_code,
        },
      };
    }

    default:
      return null;
  }
};
